import '../../../../assets/css/screens/serviceprovider/dashboard/myearnings/MyEarnings.css';
import React, { useState, useEffect, useContext } from "react";
import {
  ArrowLeft, Calendar, Wallet, Edit3, ArrowDownToLine,
  CheckCircle2, AlertTriangle, Clock, ShieldAlert,
  ChevronLeft, ChevronRight, ArrowUpRight, ArrowDownLeft,
  ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import AppContext from "../../../../context/AppContext";

const Earnings = () => {
  const { baseUrl } = useContext(AppContext);
  const navigate = useNavigate();
  const authToken = localStorage.getItem("authToken");

  /* ── Connect Account states ── */
  const [connecting, setConnecting] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [accountStatus, setAccountStatus] = useState('not_connected');
  // 'not_connected' | 'pending' | 'active' | 'restricted'

  /* ── Withdraw states ── */
  const [withdrawing, setWithdrawing] = useState(false);

  /* ── Transactions / Payment history states ── */
  const [transactions, setTransactions] = useState([]);
  const [txnLoading, setTxnLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  /* ═══════════════════════════════════════
     CONNECT ACCOUNT — ORIGINAL LOGIC (UNCHANGED)
     ═══════════════════════════════════════ */
  const handleConnectAccount = async () => {
    try {
      setConnecting(true);

      const response = await axios.post(
        `${baseUrl}/worker/connect-account`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.log(error.response?.data);

      alert(
        error.response?.data?.message ||
        "Unable to connect Stripe account."
      );
    } finally {
      setConnecting(false);
    }
  };

  /* ═══════════════════════════════════════
     CHECK ONBOARDING STATUS
     ═══════════════════════════════════════ */
  const fetchAccountStatus = () => {
    setStatusLoading(true);

    axios.get(
      `${baseUrl}/worker/check-onboarding-status`,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    )
      .then((res) => {

        const {
          details_submitted,
          charges_enabled,
          payouts_enabled
        } = res.data;

        if (
          details_submitted &&
          charges_enabled &&
          payouts_enabled
        ) {

          setAccountStatus('active');

        } else if (
          !details_submitted ||
          !payouts_enabled
        ) {

          setAccountStatus('pending');

        } else {

          setAccountStatus('restricted');
        }

      })
      .catch((err) => {

        console.log(
          err.response?.data || err.message
        );

        setAccountStatus('not_connected');

      })
      .finally(() => {
        setStatusLoading(false);
      });
  };



  /* ═══════════════════════════════════════
     EDIT ACCOUNT — Stripe Express Login Link
     ═══════════════════════════════════════ */
  const handleEditAccount = async () => {
    try {
      setConnecting(true);

      const response = await axios.get(
        `${baseUrl}/worker/edit-account`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.data.success && response.data.url) {
        window.location.href = response.data.url;
      }

    } catch (error) {

      console.log(
        error.response?.data || error.message
      );

      alert(
        error.response?.data?.message ||
        "Unable to open Stripe account settings."
      );

    } finally {
      setConnecting(false);
    }
  };


  const fetchBalance = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/worker/balance`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.data.success) {
        setAvailableBalance(response.data.available || 0);
      }

    } catch (error) {
      console.log(
        error.response?.data || error.message
      );
    }
  };

 const handleWithdraw = () => {
  if (!availableBalance || availableBalance <= 0) {
    alert("No available balance to withdraw.");
    return;
  }

  setWithdrawing(true);

  axios.post(
    `${baseUrl}/worker/withdraw`,
    {},
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    }
  )
    .then((res) => {
      if (res.data.success) {
        alert(
          res.data.message ||
          "Withdrawal request created successfully."
        );

        // Refresh available balance
        fetchBalance();

        // Refresh transaction history
        fetchTransactions(1);

        // Refresh account status
        fetchAccountStatus();
      }
    })
    .catch((err) => {
      console.log(err.response?.data || err.message);

      alert(
        err.response?.data?.message ||
        "Withdrawal failed."
      );
    })
    .finally(() => {
      setWithdrawing(false);
    });
};

  /* ═══════════════════════════════════════
     PAYMENT HISTORY (paginated)
     ═══════════════════════════════════════ */
  const fetchTransactions = (page = 1) => {
    setTxnLoading(true);

    axios.get(`${baseUrl}/worker/transaction-history`, {
      params: { page },
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then((res) => {
        setTransactions(res.data.transactions || []);
        setCurrentPage(res.data.current_page || 1);
        setTotalPages(res.data.last_page || 1);
      })
      .catch((err) => {
        console.log(err.response?.data || err.message);
        setTransactions([]);
      })
      .finally(() => setTxnLoading(false));
  };

  useEffect(() => {
    fetchAccountStatus();
    fetchTransactions(1);
    fetchBalance();
  }, []);

  /* ── Status card content mapping ── */
  const statusMeta = {
    not_connected: {
      icon: <Wallet size={20} />,
      label: 'Not Connected',
      color: '#9ca3af',
      bg: 'rgba(156,163,175,0.12)'
    },
    pending: {
      icon: <Clock size={20} />,
      label: 'Setup Incomplete',
      color: '#f59e0b',
      bg: 'rgba(245,158,11,0.12)'
    },
    active: {
      icon: <CheckCircle2 size={20} />,
      label: 'Active',
      color: '#10b981',
      bg: 'rgba(16,185,129,0.12)'
    },
    restricted: {
      icon: <ShieldAlert size={20} />,
      label: 'Restricted',
      color: '#ef4444',
      bg: 'rgba(239,68,68,0.12)'
    }
  };

  const meta = statusMeta[accountStatus];

  /* ── Connect button label logic ── */
  const getConnectBtnLabel = () => {
    if (connecting) return "Connecting...";
    if (accountStatus === 'pending') return "Continue Setup";
    if (accountStatus === 'restricted') return "Resolve Issue";
    return "Connect Account";
  };

  return (
    <div className="ew-page">

      {/* Header */}
      <div className="ew-page-header">
        <button className="ew-page-header__back" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
        </button>
        <div className="ew-page-header__content">
          <h2 className="ew-page-title">Earnings & Wallet</h2>
        </div>
      </div>

      <div className="ew-container">

        {/* ── Account Card ── */}
        <div className="ew-account-card">
          <div className="ew-account-card__top">
            <div className="ew-account-card__left">
              <div className="ew-account-card__icon" style={{ background: meta.bg, color: meta.color }}>
                {meta.icon}
              </div>
              <div>
                <p className="ew-account-card__label">Payout Account</p>
                <h4 className="ew-account-card__title">
                  {statusLoading ? 'Checking status...' : (
                    accountStatus === 'not_connected' ? 'No Account Connected' : 'Stripe Account'
                  )}
                </h4>
              </div>
            </div>

            {!statusLoading && (
              <span className="ew-status-badge" style={{ color: meta.color, background: meta.bg }}>
                {meta.icon} {meta.label}
              </span>
            )}
          </div>

          {accountStatus === 'restricted' && (
            <div className="ew-alert ew-alert--warning">
              <AlertTriangle size={16} />
              <span>Your account has restrictions. Please complete the required steps to resume payouts.</span>
            </div>
          )}

          <p className="ew-account-card__desc">
            {accountStatus === 'active'
              ? "Your payout account is active. You can withdraw your earnings anytime."
              : "Link your payout account with Picks4Tips to receive payments for completed jobs."}
          </p>

          {/* Available Balance = amount that will be withdrawn */}
          {accountStatus === 'active' && (
            <div className="ew-balance-box">
              <span>Available to Withdraw</span>
              <strong>
                ${availableBalance.toFixed(2)}
              </strong>
            </div>
          )}

          <div className="ew-account-card__actions">
            {accountStatus !== 'active' && (
              <button
                className="ew-btn ew-btn--primary"
                onClick={handleConnectAccount}
                disabled={connecting || statusLoading}
              >
                {connecting ? <span className="ew-spinner" /> : <Wallet size={16} />}
                {getConnectBtnLabel()}
              </button>
            )}

            {accountStatus === 'active' && (
              <>

                <button
                  className="ew-btn ew-btn--outline"
                  onClick={handleEditAccount}
                  disabled={connecting}
                >
                  {connecting ? (
                    <span className="ew-spinner" />
                  ) : (
                    <Edit3 size={16} />
                  )}

                  {connecting ? "Opening Stripe..." : "Edit Account"}

                  {!connecting && <ExternalLink size={13} />}
                </button>


               <button 
  className="ew-btn ew-btn--success" 
  onClick={handleWithdraw} 
  disabled={
    withdrawing ||
    availableBalance <= 0
  } 
> 
  {withdrawing ? (
    <span className="ew-spinner" />
  ) : (
    <ArrowDownToLine size={16} />
  )} 

  {withdrawing ? "Processing..." : "Withdraw All"} 
</button>
              </>
            )}
          </div>
        </div>

        {/* ── Payment History ── */}
        <div className="ew-history-card">
          <div className="ew-history-card__header">
            <h5 className="ew-history-card__title">
              <Calendar size={17} /> Payment History
            </h5>
          </div>

          {txnLoading ? (
            <div className="ew-history-empty">
              <div className="ew-spinner ew-spinner--dark" />
              <p>Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="ew-history-empty">
              <Wallet size={30} />
              <p>No transactions yet</p>
            </div>
          ) : (
            <>
              <div className="ew-txn-list">
                {transactions.map((txn) => (
                 <div key={txn.id} className="ew-txn-item">

  <div className="ew-txn-icon ew-txn-icon--debit">
    <ArrowDownLeft size={17} />
  </div>

  <div className="ew-txn-info">
    <p className="ew-txn-title">
      {txn.title || "Withdrawal"}
    </p>

    <p className="ew-txn-date">
      {txn.date}
    </p>

   <p className={`ew-txn-status ew-txn-status--${String(txn.status).toLowerCase()}`}>
  Status: {txn.status}
</p>
  </div>

  <span className="ew-txn-amount ew-txn-amount--debit">
    -${Number(txn.amount).toFixed(2)}
  </span>

</div>
                ))}
              </div>

              {/* ── Pagination ── */}
              {totalPages > 1 && (
                <div className="ew-pagination">
                  <button
                    className="ew-pagination__btn"
                    disabled={currentPage === 1}
                    onClick={() => fetchTransactions(currentPage - 1)}
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <span className="ew-pagination__info">
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    className="ew-pagination__btn"
                    disabled={currentPage === totalPages}
                    onClick={() => fetchTransactions(currentPage + 1)}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

      </div>

    </div>
  );
};

export default Earnings;